// Iter 82.10 / Phase E3.1 Sprint 3 — Payslips panel inside Run Console.
//
// 3 sub-views combined: Generation + Send actions, Payslip list with
// regeneration + admin download, and Delivery Dashboard pivot.
import { useCallback, useEffect, useState } from "react";
import {
    Download, ArrowsClockwise, PaperPlaneRight, Files, Warning,
    CheckCircle, Clock,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";

const STATUS_TONE = {
    Pending: "bg-slate-100 text-slate-700",
    Generated: "bg-blue-100 text-blue-800",
    SendQueued: "bg-indigo-100 text-indigo-700",
    Sent: "bg-emerald-100 text-emerald-800",
    Delivered: "bg-emerald-100 text-emerald-900",
    Bounced: "bg-rose-100 text-rose-800",
    BouncedSpam: "bg-rose-100 text-rose-900",
    OptedOut: "bg-zinc-100 text-zinc-700",
    Superseded: "bg-amber-50 text-amber-700",
};

// Statuses for which a per-employee re-send is meaningful.
const SEND_ELIGIBLE = new Set(["Generated", "Bounced", "BouncedSpam"]);

export default function PayrollPayslipsPanel({ runId, run, canEdit, onRefresh }) {
    const [payslips, setPayslips] = useState([]);
    const [delivery, setDelivery] = useState({ rows: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [regenTarget, setRegenTarget] = useState(null);
    const [regenReason, setRegenReason] = useState("");

    const isLockedOrAfter = run?.status === "Locked" || run?.status === "Disbursed";

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [pl, dd] = await Promise.all([
                api.get(`/payroll/run/${runId}/payslips/list`),
                api.get(`/payroll/run/${runId}/delivery-dashboard`),
            ]);
            setPayslips(pl.data.items || []);
            setDelivery(dd.data || { rows: [], summary: {} });
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load payslips"));
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => { reload(); }, [reload]);

    const generate = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/payroll/run/${runId}/payslips/generate`, {
                only_for_employee_ids: [],
            });
            const { generated_count, failure_count } = r.data || {};
            if (failure_count > 0) {
                toast.error(`Generated ${generated_count} · ${failure_count} failed (see logs)`);
            } else {
                toast.success(`Generated ${generated_count} payslip(s)`);
            }
            await reload();
            onRefresh && onRefresh();
        } catch (err) {
            toast.error(formatApiError(err, "Bulk generate failed"));
        } finally {
            setBusy(false);
        }
    };

    const send = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/payroll/run/${runId}/payslips/send`, {
                only_for_payslip_ids: [],
            });
            const { sent_count, failed_count } = r.data || {};
            if (failed_count > 0) {
                toast.error(`Sent ${sent_count} · ${failed_count} failed`);
            } else {
                toast.success(`Sent ${sent_count} payslip(s)`);
            }
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Bulk send failed"));
        } finally {
            setBusy(false);
        }
    };

    const downloadAdmin = async (ps) => {
        try {
            const r = await api.get(`/payroll/payslip/${ps.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Payslip_${ps.employee_code_snapshot}_${ps.period_month_snapshot}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    const confirmRegen = async () => {
        if (regenReason.length < 10) {
            toast.error("Reason must be at least 10 characters");
            return;
        }
        try {
            await api.post(`/payroll/payslip/${regenTarget.id}/regenerate`, { reason: regenReason });
            toast.success("Payslip regenerated");
            setRegenTarget(null);
            setRegenReason("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Regeneration failed"));
        }
    };

    // Iter 82.14 / Phase E3.2 Sprint 4 — Per-employee Send Payslip CTA.
    // Reuses bulk-send endpoint with only_for_payslip_ids=[id]. Visible
    // only for Generated/Bounced/BouncedSpam payslips (re-send semantics).
    const sendOne = async (ps) => {
        if (!ps?.id) return;
        setBusy(true);
        try {
            const r = await api.post(`/payroll/run/${runId}/payslips/send`, {
                only_for_payslip_ids: [ps.id],
            });
            const { sent_count, failed_count } = r.data || {};
            if (failed_count > 0) {
                toast.error(`Send failed for ${ps.employee_code_snapshot}`);
            } else if (sent_count > 0) {
                toast.success(`Sent payslip to ${ps.employee_code_snapshot}`);
            } else {
                toast.message("No-op (payslip already sent or not eligible).");
            }
            await reload();
            onRefresh && onRefresh();
        } catch (err) {
            toast.error(formatApiError(err, "Per-employee send failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4 pt-3" data-testid="payslips-panel">
            {!isLockedOrAfter && (
                <div className="border bg-amber-50 border-amber-200 text-amber-900 text-xs px-3 py-2 rounded" data-testid="payslips-locked-notice">
                    Run is in <strong>{run?.status}</strong>. Payslips can be generated only after the
                    run is <strong>Locked</strong> (operator-locked Sprint 3 control).
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-600">
                    <Files size={14} className="inline mr-1" /> {payslips.length} payslip(s) on this run
                </span>
                <span className="flex-1" />
                {canEdit && isLockedOrAfter && (
                    <>
                        <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="payslips-generate-btn">
                            <Files size={14} className="mr-1" /> Generate (bulk)
                        </Button>
                        <Button size="sm" onClick={send} disabled={busy} data-testid="payslips-send-btn">
                            <PaperPlaneRight size={14} className="mr-1" /> Send (bulk)
                        </Button>
                    </>
                )}
            </div>

            {/* Delivery summary strip */}
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="delivery-summary">
                {["Pending", "Generated", "Sent", "Bounced", "OptedOut", "Superseded"].map((s) => {
                    const n = (delivery.summary || {})[s] || 0;
                    return (
                        <div key={s} className="border rounded-md px-2 py-1.5 text-center bg-white" data-testid={`delivery-summary-${s}`}>
                            <div className="text-[10px] uppercase text-slate-500">{s}</div>
                            <div className="text-base font-mono font-semibold">{n}</div>
                        </div>
                    );
                })}
            </div>

            {/* Delivery dashboard rows */}
            <div className="border rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="delivery-table">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Employee</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Last Sent</th>
                            <th className="px-3 py-2 text-left">Last ESS Download</th>
                            <th className="px-3 py-2 text-right">Downloads</th>
                            <th className="px-3 py-2 text-right">Regens</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
                        {!loading && (delivery.rows || []).length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="delivery-empty">No employees in this run.</td></tr>
                        )}
                        {(delivery.rows || []).map((row) => {
                            const ps = payslips.find((p) => p.id === row.payslip_id);
                            return (
                                <tr key={row.employee_id} className="hover:bg-slate-50" data-testid={`delivery-row-${row.employee_code}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800 text-sm">{row.employee_name}</div>
                                        <div className="text-[10px] font-mono text-slate-400">{row.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge className={`${STATUS_TONE[row.payslip_status] || "bg-slate-100"}`}>{row.payslip_status}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{row.last_sent_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{row.last_ess_download_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                                    <td className="px-3 py-2 text-right text-xs font-mono">{row.downloaded_count || 0}</td>
                                    <td className="px-3 py-2 text-right text-xs font-mono">{row.regeneration_count || 0}</td>
                                    <td className="px-3 py-2 text-right">
                                        {ps && (
                                            <div className="inline-flex gap-1">
                                                <Button size="sm" variant="ghost" onClick={() => downloadAdmin(ps)} data-testid={`delivery-download-${row.employee_code}`}>
                                                    <Download size={12} />
                                                </Button>
                                                {canEdit && SEND_ELIGIBLE.has(row.payslip_status) && isLockedOrAfter && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        disabled={busy}
                                                        onClick={() => sendOne(ps)}
                                                        title="Send / Re-send this payslip"
                                                        data-testid={`delivery-send-${row.employee_code}`}
                                                    >
                                                        <PaperPlaneRight size={12} />
                                                    </Button>
                                                )}
                                                {canEdit && (
                                                    <Button size="sm" variant="ghost" onClick={() => setRegenTarget(ps)} data-testid={`delivery-regen-${row.employee_code}`}>
                                                        <ArrowsClockwise size={12} />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!regenTarget} onOpenChange={(v) => !v && setRegenTarget(null)}>
                <DialogContent data-testid="regen-dialog">
                    <DialogHeader>
                        <DialogTitle>Regenerate payslip</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            The existing payslip will be marked Superseded — kept forever
                            for the audit trail — and a new one will be issued.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label className="text-xs">Reason (≥10 chars, mandatory)</Label>
                        <Input
                            value={regenReason}
                            onChange={(e) => setRegenReason(e.target.value)}
                            placeholder="e.g. Late variable bonus added by Finance; recompute requested by employee."
                            data-testid="regen-reason"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRegenTarget(null)} data-testid="regen-cancel">Cancel</Button>
                        <Button onClick={confirmRegen} data-testid="regen-confirm">Regenerate + Supersede</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
